import {useRef, useState, useEffect} from 'react'
import { 
        Player,
        ControlBar, 
        ForwardControl, 
        ReplayControl, 
        FullscreenToggle, 
        PlayToggle,
        VolumeMenuButton, 
        CurrentTimeDisplay, 
        TimeDivider, 
        DurationDisplay, 
        ProgressControl, 
        PlaybackRateMenuButton 
      } from 'video-react-player';
import 'video-react-player/dist/video-react-player.css';
import s from './index.module.css'
import { PiPIcon } from '@/utils/icons';
import classNames from 'classnames';
import { useBlockDownloads } from '@/hooks/useBlockDownloads';
import { university2035DigitalTraceApi } from '@/api';

const VideoPlayer = ({
  src,
  prohibitDownloading = false,
  digitalTraceContext = null,
  hideFullscreen = false,
}) => {

  const playerRef = useRef(null);
  const traceContextRef = useRef(digitalTraceContext);
  const traceStartedAtRef = useRef(null);
  const segmentStartRef = useRef(null);
  const pendingIntervalsRef = useRef([]);
  const attachedVideoRef = useRef(null);
  const [isPiPActive, setIsPiPActive] = useState(false);

  useEffect(() => {
    traceContextRef.current = digitalTraceContext;
  }, [digitalTraceContext]);

  useEffect(() => {
    if (!('pictureInPictureEnabled' in document)) return;

    const handlePiPChange = () => {
      setIsPiPActive(document.pictureInPictureElement !== null);
    };

    document.addEventListener('enterpictureinpicture', handlePiPChange);
    document.addEventListener('leavepictureinpicture', handlePiPChange);

    return () => {
      document.removeEventListener('enterpictureinpicture', handlePiPChange);
      document.removeEventListener('leavepictureinpicture', handlePiPChange);
    };
  }, []);

  const togglePictureInPicture = async () => {
    if (!playerRef.current) return;

    try {
      if (isPiPActive) {
        await document.exitPictureInPicture();
      } else {
        const videoElement = playerRef.current.video?.video;
        if (videoElement) {
          await videoElement.requestPictureInPicture();
        }
      }
    } catch (error) {
      console.error('PiP error:', error);
    }
  };
  
  const [showMenu, setShowMenu] = useState(false);


  useBlockDownloads(
    { 
      videoElement: playerRef.current?.video?.video, 
      shouldBlock: prohibitDownloading
    }
  )

  useEffect(() => {
    if (!digitalTraceContext?.lessonId || !digitalTraceContext?.videoId) return;

    const flush = (videoElement, force = false) => {
      const context = traceContextRef.current;
      if (!context?.lessonId || !context?.videoId || !videoElement) return;

      const current = Number(videoElement.currentTime || 0);
      const start = segmentStartRef.current;
      if (start !== null && current > start) {
        pendingIntervalsRef.current.push({ start, end: current });
      }
      segmentStartRef.current = videoElement.paused || videoElement.ended ? null : current;

      const covered = pendingIntervalsRef.current.reduce((sum, item) => sum + Math.max(0, item.end - item.start), 0);
      if (!force && covered < 15) return;
      if (!pendingIntervalsRef.current.length) return;

      const intervals = pendingIntervalsRef.current;
      pendingIntervalsRef.current = [];

      university2035DigitalTraceApi.trackVideoProgress({
        lesson_id: context.lessonId,
        video_id: context.videoId,
        duration: Number.isFinite(videoElement.duration) ? videoElement.duration : null,
        position: current,
        started_at: traceStartedAtRef.current,
        intervals,
      }).catch(() => {});
    };

    const attach = () => {
      const videoElement = playerRef.current?.video?.video;
      if (!videoElement || attachedVideoRef.current === videoElement) return;

      attachedVideoRef.current = videoElement;

      const onPlay = () => {
        if (!traceStartedAtRef.current) {
          traceStartedAtRef.current = new Date().toISOString();
        }
        segmentStartRef.current = Number(videoElement.currentTime || 0);
      };
      const onPause = () => flush(videoElement, true);
      const onEnded = () => flush(videoElement, true);
      const onTimeUpdate = () => flush(videoElement, false);

      videoElement.addEventListener('play', onPlay);
      videoElement.addEventListener('pause', onPause);
      videoElement.addEventListener('ended', onEnded);
      videoElement.addEventListener('timeupdate', onTimeUpdate);

      return () => {
        flush(videoElement, true);
        videoElement.removeEventListener('play', onPlay);
        videoElement.removeEventListener('pause', onPause);
        videoElement.removeEventListener('ended', onEnded);
        videoElement.removeEventListener('timeupdate', onTimeUpdate);
      };
    };

    let detach = attach();
    const timer = window.setInterval(() => {
      if (!detach) detach = attach();
    }, 500);

    return () => {
      window.clearInterval(timer);
      if (detach) detach();
      attachedVideoRef.current = null;
      segmentStartRef.current = null;
      pendingIntervalsRef.current = [];
      traceStartedAtRef.current = null;
    };
  }, [digitalTraceContext?.lessonId, digitalTraceContext?.videoId, src]);

  return (
    <Player
      ref={playerRef}
      src={src}
      fluid={true} // адаптируется под ширину
      playsInline // ВАЖНО для iPhone
      aspectRatio="16:9"
      startTime={0}
      className={s.player}
    >
      <ControlBar>
        <ReplayControl seconds={10} order={1} />
        <ForwardControl seconds={10} order={2} />
        <PlayToggle order={3} />
        <VolumeMenuButton order={4} />
        <CurrentTimeDisplay order={5} />
        <TimeDivider order={6} />
        <DurationDisplay order={7} />
        <ProgressControl order={8} />

        <PlaybackRateMenuButton
          rates={[0.25, 0.5, 1, 1.5, 2, 3]}
          order={9} 
          className={classNames(s.nemuItem, s.nemuItemFirst, {[s.nemuItemHide]: !showMenu})}
        />
        {!hideFullscreen && (
          <FullscreenToggle
            className={classNames(s.nemuItem, s.nemuItemThird, {[s.nemuItemHide]: !showMenu})}
          />
        )}
        <div order={10}>
          {'pictureInPictureEnabled' in document && (
            <button
              onClick={togglePictureInPicture}
              className={classNames(s.nemuItem, s.nemuItemSecond, {[s.nemuItemHide]: !showMenu})}
              title={isPiPActive ? 'Выйти из режима картинка в картинке' : 'Картинка в картинке'}
            >
              <PiPIcon active={isPiPActive} />
            </button>
          )}
          <button onClick={()=>setShowMenu(prev => !prev)} className={s.menu}>...</button>
        </div>
      </ControlBar>
    </Player>
  );
};

export default VideoPlayer;
